// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FloatingTooltip, { FloatingTooltipStyles } from "@sc/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyle } from "@sc/App/Components/Header/HeaderClickable";
import MyAccountOverlay from "@sc/App/Components/MyAccountOverlay/MyAccountOverlay";
import { xHeaderUpdate } from "@sc/App/Handlers/XNG/Header";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { canSeeInvoiceHistoryLink, xIsAuthenticated } from "@sc/App/Store/Selectors/GeneralSelectors";
import MyAccount from "@sc/XDL/Icons/MyAccount";
import MyAccountFocused from "@sc/XDL/Icons/MyAccountFocused";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /* <XNG-Changes> */
    orderManagementSectionTitle = "orderManagementSectionTitle",
    orderManagementLinks = "orderManagementLinks",
    accountManagementTitle = "accountManagementTitle",
    accountManagementLinks = "accountManagementLinks",
}

/* <XNG-Changes> */
export interface MyAccountExtraLinks {
    fields: {
        destination: LinkFieldValue;
        overrideTitle: string;
    };
}
/* <XNG-Changes> */
export interface MappedLink {
    title: string;
    url: string;
    type?: string;
}

/* <XNG-Changes> */
export const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return (
        widgetLinks
            ?.map(widgetLink => {
                if (widgetLink.fields.destination.type === "Page") {
                    const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                    if (pageLink) {
                        return {
                            ...pageLink,
                            title: widgetLink.fields.overriddenTitle || pageLink?.title,
                            openInNewWindow: widgetLink.fields.openInNewWindow,
                        };
                    }
                } else if (widgetLink.fields.destination.type === "Url") {
                    const url = widgetLink.fields.destination.value;
                    const title = widgetLink.fields.overriddenTitle;
                    const openInNewWindow = widgetLink.fields.openInNewWindow;

                    if (
                        (title.toLowerCase().indexOf("invoice history") !== -1 || url.indexOf("billtrust") > -1) &&
                        !canSeeInvoiceHistoryLink(state)
                    ) {
                        return undefined;
                    }

                    return { id: "", url, title, openInNewWindow };
                }
                return undefined;
            })
            ?.filter(pageLink =>
                (pageLink as any)?.parentId === myAccountPage?.id ? !(pageLink as any)?.excludeFromNavigation : true,
            ) || []
    );
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const orderManagementLinks = mapLinks(state, ownProps.fields.orderManagementLinks);
    const accountManagementLinks = mapLinks(state, ownProps.fields.accountManagementLinks);
    const linkGroups = [
        { title: ownProps.fields.orderManagementSectionTitle, links: orderManagementLinks },
        { title: ownProps.fields.accountManagementTitle, links: accountManagementLinks },
    ];
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-Change
        isAuthenticated: xIsAuthenticated(state),
        linkGroups,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    xHeaderUpdate,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Change
    headerClickable?: HeaderClickableStyle;
    tippyStyles?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        /* <XNG-Changes> */
        [fields.orderManagementSectionTitle]: string;
        [fields.orderManagementLinks]: LinkModel[];
        [fields.accountManagementTitle]: string;
        [fields.accountManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    isAuthenticated,
    xHeaderUpdate,
    linkGroups,
}: Props) => {
    // XNG-Change
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    // XNG-Change
    if (!myAccountPageLink || !isAuthenticated) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = MyAccount;
    }
    let menuItems = myAccountPageLink?.children?.filter(pageLink => !pageLink.excludeFromNavigation);

    const currentMode = getCookie(NavigationModeCookieName);
    // let menuItems = (myAccountPageLink?.children?.filter(
    //     pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    // ) ?? []) as MappedLink[];

    // const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
    //     return {
    //         title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
    //         iconProps: {
    //             src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
    //             size: 22,
    //             css: css`
    //                 margin-left: 20px;
    //                 margin-right: -12px;
    //             `,
    //         },
    //         clickableProps: {
    //             css: css`
    //                 display: flex;
    //                 text-transform: uppercase;
    //             `,
    //             onClick: () => {
    //                 title === "Inventory"
    //                     ? setShowInventory({ showInventory: !showInventory })
    //                     : setShowPricing({ showPricing: !showPricing });
    //             },
    //         },
    //     };
    // };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        //     showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        //     showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    // XNG-Changes: this function is fully customized and the OOTB code been removed to handle the sign in and sign out functionality based on the requirements.
    const onSignInHandler = (e: React.MouseEvent) => {
        history.push(myAccountPageLink?.url || "/MyAccount");
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };
    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const handleOpenChange = (open: boolean) => {
        setIsTooltipOpen(open);
        setIsPopupShown(open);

        if (open) {
            xHeaderUpdate({ key: "registerMenu", value: `header_signin_tooltip` });
        } else {
            xHeaderUpdate({ key: "unregisterMenu", value: `header_signin_tooltip` });
        }
    };

    // XNG-Changes
    const signInClickable = (
        <HeaderClickable
            iconProps={{ src: isPopupShown ? MyAccountFocused : icon }}
            onClick={onSignInHandler}
            data-test-selector="header_signIn"
            title={translate("My Account")}
            isOpen={isPopupShown}
            extendedStyles={styles.headerClickable}
        />
    );

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    return (
        <StyledWrapper>
            <Hidden below="lg">
                <FloatingTooltip
                    triggerComponent={signInClickable}
                    isOpen={isTooltipOpen}
                    onOpenChange={handleOpenChange}
                    placement="bottom-end"
                    offset={{ mainAxis: 10, crossAxis: 0 }}
                    trigger="hover"
                    showArrow={false}
                    overlay={true}
                    extendedStyles={styles.tippyStyles}
                >
                    <MyAccountOverlay linkGroups={linkGroups} onSignOutHandler={onSignOutHandler} />
                </FloatingTooltip>
            </Hidden>
            <Hidden above="md">{signInClickable}</Hidden>
        </StyledWrapper>
    );
};

// XNG-Change
export const linksFieldDefinitions = [
    {
        name: fields.accountManagementTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Account Management",
    },
    {
        displayName: "Links",
        name: fields.accountManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
    {
        name: fields.orderManagementSectionTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Product & Order Management",
    },
    {
        displayName: "Links",
        name: fields.orderManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            // {
            //     name: fields.visibilityState,
            //     displayName: "Settings",
            //     editorTemplate: "RadioButtonsField",
            //     options: [
            //         {
            //             displayName: "Both",
            //             value: "both",
            //         },
            //         {
            //             displayName: "Show Label",
            //             value: "label",
            //         },
            //         {
            //             displayName: "Show Icon",
            //             value: "icon",
            //         },
            //     ],
            //     defaultValue: "both",
            //     fieldType: "General",
            //     sortOrder: 1,
            // },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.includeAccountMenu,
            //     displayName: "Include My Account Menu",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 3,
            //     isVisible: widget => false,
            // },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => false,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => false,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
            // XNG-Changes
            ...linksFieldDefinitions,
        ] as FieldDefinition[],
        group: "Common",
    },
};

export default widgetModule;
